import { useConsumptionData } from "@/api/consumption";
import { useCreateAlert } from "@/api/createAlert";
import { useAlertData } from "@/api/getAlert";
import { useUpdateAlert } from "@/api/updateAlert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatCurrency } from "@/lib/format-for-view";
import { format, parseISO, startOfMonth, sub } from "date-fns";
import { Euro, Pencil, TrendingDown, TrendingUp } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

export default function BudgetSummary() {
	const [dialogOpen, setDialogOpen] = useState(false);
	const [kwhGoal, setKwhGoal] = useState("");
	const [euroBudget, setEuroBudget] = useState("");
	const [alertsEnabled, setAlertsEnabled] = useState(false);
	const [unit, setUnit] = useState<"kWh" | "EUR">("EUR");
	const [isEditing, setIsEditing] = useState(false);

	const { mutate: createAlert } = useCreateAlert();
	const { mutate: updateAlert } = useUpdateAlert();

	// Calculate dates for current period
	const twoDaysAgo = sub(new Date(), { days: 2 });
	const startOfCurrentMonth = startOfMonth(twoDaysAgo);
	const dayOfMonthTwoDaysAgo = twoDaysAgo.getDate();

	// Calculate dates for previous month same period
	const startOfPreviousMonth = startOfMonth(
		sub(startOfCurrentMonth, { days: 1 }),
	);
	const endOfPreviousMonthPeriod = sub(startOfPreviousMonth, {
		days: -dayOfMonthTwoDaysAgo + 1,
	});

	// Fetch current month data
	const {
		data: apiData,
		isLoading: isCurrentLoading,
		error: currentError,
	} = useConsumptionData({
		energy: "ELEC",
		granularity: "DAILY",
		start_date: format(startOfPreviousMonth, "yyyy-MM-dd"),
		end_date: format(twoDaysAgo, "yyyy-MM-dd"),
	});

	const { data: alertData, isLoading: isAlertLoading } = useAlertData({
		energy: "ELEC",
	});

	const hasAlerts =
		alertData?.alerts_details?.some(
			(alert) =>
				alert.alert_type === "USAGE_LIMIT_CONSUMPTION_MONTHLY" ||
				alert.alert_type === "USAGE_LIMIT_COST_MONTHLY",
		) ?? false;

	const currentAlert =
		alertData?.alerts_details?.find(
			(alert) =>
				alert.alert_type === "USAGE_LIMIT_CONSUMPTION_MONTHLY" ||
				alert.alert_type === "USAGE_LIMIT_COST_MONTHLY",
		) ?? null;
	const alertValue = currentAlert?.value ?? null;
	const alertUnit = currentAlert?.unit ?? null;

	const currentMonthData = apiData?.data.filter(
		(item) => parseISO(item.timestamp) >= startOfCurrentMonth,
	);

	const previousMonthData = apiData?.data.filter(
		(item) => parseISO(item.timestamp) <= endOfPreviousMonthPeriod,
	);

	// Calculate current month totals
	const currentUsage =
		currentMonthData?.reduce(
			(sum: number, item: { consumption: number }) => sum + item.consumption,
			0,
		) ?? 0;

	const currentCost =
		currentMonthData?.reduce(
			(sum: number, item: { cost: number; subscription_cost: number }) =>
				sum + item.cost + item.subscription_cost,
			0,
		) ?? 0;

	const previousCost =
		previousMonthData?.reduce(
			(sum: number, item: { cost: number; subscription_cost: number }) =>
				sum + item.cost + item.subscription_cost,
			0,
		) ?? 0;

	// Check if we have previous month data
	const hasPreviousData = previousMonthData && previousMonthData.length > 0;

	// Calculate cost percentage change
	let costChange = 0;
	let isIncrease = false;

	if (hasPreviousData && previousCost > 0) {
		costChange = ((currentCost - previousCost) / previousCost) * 100;
		isIncrease = costChange > 0;
	}

	// Calculate progress based on the alert unit and value from the API
	const progress =
		alertValue && alertUnit
			? alertUnit === "kWh"
				? (currentUsage / alertValue) * 100
				: alertUnit === "EUR"
					? (currentCost / alertValue) * 100
					: 0
			: 0;

	const exceeded = alertValue && progress > 100;

	const handleOpenDialog = (editing = false) => {
		setIsEditing(editing);
		if (editing && currentAlert) {
			if (currentAlert.unit === "kWh") {
				setKwhGoal(currentAlert.value.toString());
				setEuroBudget("");
			} else {
				setEuroBudget(currentAlert.value.toString());
				setKwhGoal("");
			}
			setUnit(currentAlert.unit as "kWh" | "EUR");
			setAlertsEnabled(true);
		} else {
			setKwhGoal("");
			setEuroBudget("");
			setUnit("kWh");
			setAlertsEnabled(false);
		}
		setDialogOpen(true);
	};

	const handleSubmit = () => {
		if (isEditing && currentAlert) {
			// Update existing alert
			if (unit === "kWh") {
				updateAlert({
					alert_type: "USAGE_LIMIT_CONSUMPTION_MONTHLY",
					unit: "kWh",
					alert_id: currentAlert.alert_id,
					energy: "ELEC",
					value: Number(kwhGoal),
					is_active: true,
				});
			} else {
				updateAlert({
					alert_type: "USAGE_LIMIT_COST_MONTHLY",
					unit: "EUR",
					alert_id: currentAlert.alert_id,
					energy: "ELEC",
					value: Number(euroBudget),
					is_active: true,
				});
			}
		} else {
			// Create new alert
			createAlert({
				energy: "ELEC",
				alert_type:
					unit === "kWh"
						? "USAGE_LIMIT_CONSUMPTION_MONTHLY"
						: "USAGE_LIMIT_COST_MONTHLY",
				value: unit === "kWh" ? Number(kwhGoal) : Number(euroBudget),
				unit: unit,
			});
		}
		setDialogOpen(false);
	};

	// Check if we have any data to display
	if (currentMonthData?.length === 0) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Aucune donnée trouvée pour cette période
					</div>
				</CardContent>
			</Card>
		);
	}

	if (isCurrentLoading || isAlertLoading) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="animate-pulse space-y-4">
						<div className="h-4 bg-gray-200 rounded w-1/4" />
						<div className="h-6 bg-gray-200 rounded w-3/4" />
						<div className="h-8 bg-gray-200 rounded w-1/2" />
						<div className="h-4 bg-gray-200 rounded w-2/3" />
					</div>
				</CardContent>
			</Card>
		);
	}

	if (currentError) {
		console.error("Error fetching consumption data:", currentError);
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Erreur lors du chargement des données de consommation. Veuillez
						réessayer ultérieurement.
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className="w-full max-w-md">
			<CardContent className="px-3 sm:px-6 py-6">
				<div className="flex items-center justify-between mb-1">
					<div className="flex items-center gap-2">
						<div className="rounded-full p-1">
							<Euro className="w-4 h-4 text-green-500" />
						</div>
					</div>
					<span className="text-gray-400 text-xs md:text-sm">
						Dernière mise à jour le {format(twoDaysAgo, "dd/MM")}
					</span>
				</div>

				<h2 className="text-base md:text-lg font-medium mb-2">
					Résumé du budget mensuel
				</h2>

				<div className="mb-2">
					<span className="text-xl md:text-2xl font-bold">
						{unit === "kWh"
							? `${currentUsage.toFixed(2)} kWh`
							: formatCurrency(currentCost)}
					</span>
					<span className="text-gray-400 text-sm ml-2">
						depuis le début du mois
					</span>
				</div>

				{hasPreviousData && (
					<div
						className={`text-sm md:text-base mb-4 flex items-center ${isIncrease ? "text-red-600" : "text-green-600"}`}
					>
						<span>{`${isIncrease ? "+" : "-"}${Math.abs(costChange).toFixed(0)}% par rapport au mois précédent à la même période`}</span>
						{isIncrease ? (
							<TrendingUp className="w-4 h-4 ml-1" />
						) : (
							<TrendingDown className="w-4 h-4 ml-1" />
						)}
					</div>
				)}

				{!hasAlerts && (
					<Button
						className="w-full bg-pink-500 text-white mt-4"
						onClick={() => handleOpenDialog(false)}
					>
						Définir un budget
					</Button>
				)}

				{hasAlerts && (
					<>
						<div className="flex items-center justify-between mb-2">
							<span className="text-sm font-medium">
								Pourcentage de votre objectif mensuel
							</span>
							<Button
								variant="ghost"
								size="sm"
								className="h-8 w-8 p-0"
								onClick={() => handleOpenDialog(hasAlerts)}
							>
								<Pencil className="h-3 w-3" />
							</Button>
						</div>

						<div className="w-full bg-gray-200 rounded-full h-1.5 mb-1">
							<div
								className={`h-1.5 rounded-full ${
									exceeded ? "bg-red-500" : "bg-green-500"
								}`}
								style={{ width: `${Math.min(progress, 100)}%` }}
							/>
						</div>

						<div className="flex justify-between text-xs md:text-sm">
							<span>0%</span>
							<span className={exceeded ? "text-red-500" : "text-green-500"}>
								{progress.toFixed(0)}%
							</span>
						</div>
					</>
				)}

				<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
					<DialogContent className="sm:max-w-sm w-[95%] max-w-full rounded-2xl gap-0">
						<DialogHeader className="text-start px-2">
							<DialogTitle className="text-lg font-semibold">
								{isEditing ? "Modifier le budget" : "Définir un budget"}
							</DialogTitle>
						</DialogHeader>

						<div className="space-y-4 p-2">
							<Tabs defaultValue={unit} className="w-full mb-4">
								<TabsList className="w-full grid grid-cols-2">
									<TabsTrigger value="kWh" onClick={() => setUnit("kWh")}>
										kWh
									</TabsTrigger>
									<TabsTrigger value="EUR" onClick={() => setUnit("EUR")}>
										Euro
									</TabsTrigger>
								</TabsList>
							</Tabs>
							<div className="space-y-2">
								<label
									htmlFor="budget"
									className="block text-sm font-medium text-gray-700"
								>
									{unit === "kWh" ? "Consommation (kWh)" : "Budget (€)"}
								</label>
								<div className="relative">
									<Input
										id="budget"
										type="number"
										inputMode="decimal"
										value={unit === "kWh" ? kwhGoal : euroBudget}
										onChange={(e) => {
											const schema = z
												.number({ coerce: true })
												.max(999999)
												.safeParse(e.target.value);

											if (!schema.data) {
												setKwhGoal("");
												setEuroBudget("");
												return;
											}

											if (unit === "kWh") {
												setKwhGoal(schema.data?.toString() ?? "");
											} else {
												setEuroBudget(schema.data?.toString() ?? "");
											}
										}}
										placeholder={unit === "kWh" ? "0.0" : "0.00"}
										className="w-full pr-12"
									/>
									<span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
										{unit}
									</span>
								</div>
							</div>

							<div className="flex items-center space-x-2">
								<Switch
									checked={alertsEnabled}
									onCheckedChange={setAlertsEnabled}
								/>
								<p className="text-sm">Activer les alertes de dépassement</p>
							</div>

							<p className="text-sm text-gray-500">
								Vous serez alerté lorsque votre budget dépassera 90%, 100% et
								150% de la limite définie
							</p>

							<Button
								className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-lg py-2"
								onClick={handleSubmit}
							>
								Enregistrer
							</Button>
						</div>
					</DialogContent>
				</Dialog>
			</CardContent>
		</Card>
	);
}
